import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { fetchCollectionList, fetchProductList, fetchVideoGenerateList } from "@pages/library/librarySlice";
import { LanguageMapBlog } from "@src/config/language";
import { message } from "antd";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function useHideElement(className: string, callback: Function) {
  const bodyClick = (event: any) => {
    const path = event.path || (event.composedPath && event.composedPath());
    if (path && path.length >= 0) {
      for (let i = 0; i < path.length; i++) {
        if (
          path[i]?.className &&
          typeof path[i]?.className?.indexOf === "function" &&
          path[i]?.className?.indexOf(className) >= 0
        ) {
          return false;
        }
      }
    }

    callback();
  };

  // if click outside RangePicker, hidden RangePicker Component
  useEffect(() => {
    window.addEventListener("click", bodyClick);
    return () => {
      window.removeEventListener("click", bodyClick);
    };
  });
}

export const useKeyup = (callback: () => void) => {
  const handleKeyup = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      callback();
    }
  };
  useEffect(() => {
    window.addEventListener("keyup", handleKeyup);
    return () => {
      window.removeEventListener("keyup", handleKeyup);
    };
  });
};

export const useSwiper = ($wrapper: HTMLDivElement | null) => {
  const [leftBtnVisible, setLeftBtnVisible] = useState(false);
  const [rightBtnVisible, setRightBtnVisible] = useState(true);
  const [topBtnVisible, setTopBtnVisible] = useState(false);
  const [bottomBtnVisible, setBottomBtnVisible] = useState(true);
  const swipeLeft = () => {
    if ($wrapper) {
      $wrapper.scrollTo({ left: $wrapper.scrollLeft - $wrapper.clientWidth, behavior: "smooth" });
    }
  };

  const swipeRight = () => {
    if ($wrapper) {
      $wrapper.scrollTo({ left: $wrapper.scrollLeft + $wrapper.clientWidth, behavior: "smooth" });
    }
  };

  const swipeTop = () => {
    if ($wrapper) {
      $wrapper.scrollTo({ top: $wrapper.scrollTop - $wrapper.clientHeight, behavior: "smooth" });
    }
  };

  const swipeBottom = () => {
    if ($wrapper) {
      $wrapper.scrollTo({ top: $wrapper.scrollTop + $wrapper.clientHeight, behavior: "smooth" });
    }
  };

  const updateSwiperState = ($wrapper: any) => {
    if ($wrapper) {
      const updateSwiperBtn = () => {
        if ($wrapper.scrollTop > 0) {
          setTopBtnVisible(true);
        } else {
          setTopBtnVisible(false);
        }
        if ($wrapper.scrollTop + $wrapper.clientHeight < $wrapper.scrollHeight) {
          setBottomBtnVisible(true);
        } else {
          setBottomBtnVisible(false);
        }

        if ($wrapper.scrollLeft > 0) {
          setLeftBtnVisible(true);
        } else {
          setLeftBtnVisible(false);
        }

        if ($wrapper.scrollLeft + $wrapper.clientWidth < $wrapper.scrollWidth - 1) {
          setRightBtnVisible(true);
        } else {
          setRightBtnVisible(false);
        }
      };

      $wrapper.addEventListener("scrollend", () => {
        updateSwiperBtn();
      });
      $wrapper.addEventListener("resize", () => {
        updateSwiperBtn();
      });
      $wrapper.addEventListener("scroll", () => {
        updateSwiperBtn();
      });

      const ro = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target === $wrapper) {
            updateSwiperBtn();
          }
        }
      });

      ro.observe($wrapper);
    }
  };

  useEffect(() => {
    updateSwiperState($wrapper);
  }, [$wrapper]);

  return {
    leftBtnVisible,
    rightBtnVisible,
    setBottomBtnVisible,
    setTopBtnVisible,
    topBtnVisible,
    bottomBtnVisible,
    swipeLeft,
    swipeRight,
    swipeTop,
    swipeBottom,
    updateSwiperState,
  };
};

export const useDebounce = (fn: Function, timeout: number = 300) => {
  const timeRef = useRef<null | number | NodeJS.Timeout>(null);
  return (params1?: any, params2?: any) => {
    if (timeRef.current) {
      clearTimeout(timeRef.current);
    }
    timeRef.current = setTimeout(async () => {
      fn(params1, params2);
      if (timeRef.current) {
        clearTimeout(timeRef.current);
      }
    }, timeout);
  };
};

// 禁止页面滚动
export const useBodyScroll = (open: boolean) => {
  useEffect(() => {
    const $html = document.body.parentElement;
    if ($html) {
      if (open) {
        $html.style.overflow = "hidden";
      } else {
        $html.style.overflow = "overlay";
      }
    }
    return () => {
      if ($html) {
        $html.style.overflow = "overlay";
      }
    };
  }, [open]);
};

export const useKaloSchoolLink = () => {
  const { language } = useAppSelector((state) => state.globalReducer);
  const kaloSchoolLink = LanguageMapBlog[language] || LanguageMapBlog["en-US"];
  return kaloSchoolLink;
};

export const useFocusAction = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [messageApi, messageHolder] = message.useMessage();
  const navigate = useNavigate();
  const goMyFollowing = (_type: any) => {
    navigate(`/me`);
  };

  const focusSuccess = (type: string) => {
    messageApi.open({
      type: "success",
      content: (
        <div className="flex flex-row items-center">
          <Trans i18nKey="FollowedSuccessfully">
            You can view it
            <span
              className="font-medium text-kds-accent cursor-pointer mx-[4px]"
              onClick={(event) => {
                event.stopPropagation();
                goMyFollowing(type);
              }}
            >
              Explore - My Following
            </span>
            .
          </Trans>
        </div>
      ),
      duration: 3,
    });
    // dispatch(updateFeautreFocusCount(region));
  };
  const cancelFocusSuccess = async (focusType: string) => {
    if (focusType === "video-generation") message.success(t("Moved to trash."));
    else message.success(t("Unfollowed successfully!"));

    // 删除成功后刷新当前列表
    if (focusType === "video") {
      await dispatch(fetchCollectionList({ type: 1 }));
    } else if (focusType === "script") {
      await dispatch(fetchCollectionList({ type: 2 }));
    } else if (focusType === "product") {
      await dispatch(fetchProductList());
    } else if (focusType === "video-generation") {
      await dispatch(fetchVideoGenerateList({}));
    }
  };
  return {
    messageHolder,
    focusSuccess,
    cancelFocusSuccess,
  };
};

export const useItemSize = ({ gapSize, itemWidth }: { gapSize: number; itemWidth: number }) => {
  const [rowCount, setRowCount] = useState<number>(0);
  const refWrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (refWrapper.current) {
      const rowCount = Math.floor((refWrapper.current.getBoundingClientRect().width + gapSize) / (itemWidth + gapSize));

      setRowCount(rowCount);
    }
  }, []);

  return { refWrapper, rowCount };
};
